/**
 * ScoutingTabContent (modal) — player evaluation.
 *
 * Three parts (ALL LIVE — the scouting seam populates player.scouting from
 * P9's daily_signal_trajectories; see services/scouting/scouting_provider.py):
 *   1. Verdict line — the dominant skills-vs-luck headline
 *      (player.scouting.verdict.headline).
 *   2. Skills-vs-luck — skill-implied value + band vs the player's REAL stat
 *      (player.scouting.skills_vs_luck.metrics).
 *   3. Reliability — from the modal response (consistency, hot/cold, streaks,
 *      durability).
 *
 * No fabricated gaps or verdicts: players below P9's cohort floor have no
 * scouting key and render the honest "Analysis pending" / "Insufficient data"
 * states.
 */
import React from 'react';
import { dynastyTheme, withAlpha } from '../../../../../services/colorService';
import PropTypes from 'prop-types';
import { mobileColors, mobileTextStyles } from '../../../../../services/mobileStyleService';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;

const SectionHead = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 0 4px' }}>
    <span style={{ width: 3, height: 12, background: GOLD, borderRadius: 1 }} />
    <span style={{ ...mobileTextStyles.sectionHeader, color: mobileColors.textSecondary }}>{children}</span>
  </div>
);

const Row = ({ label, value, color, note }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${mobileColors.divider}` }}>
    <span style={{ fontSize: 11, color: mobileColors.textTertiary }}>{label}</span>
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      {note && <span style={{ fontSize: 9, color: mobileColors.textMuted }}>{note}</span>}
      <span style={{ fontSize: 12, fontWeight: 700, color: color || mobileColors.textPrimary }}>{value}</span>
    </span>
  </div>
);

// Honest unit formatting: ERA → 2 decimals (3.52), rate units (OPS) → .xxx.
const fmtUnit = (v, unit) =>
  v == null ? '—' : unit === 'ERA' ? v.toFixed(2) : (v >= 1 ? v.toFixed(3) : v.toFixed(3).replace(/^0/, ''));

// Option A visual: horizontal track. Dot = skill-implied value; shaded bar = the lo–hi band
// (the honest range on the skill estimate); tick = the player's REAL actual stat. Headline
// number (skill-implied) + "actual X" beside it.
const SkillTrack = ({ m }) => {
  const vals = [m.skill, m.lo, m.hi, m.actual].filter((v) => v != null);
  let lo = Math.min(...vals), hi = Math.max(...vals);
  const pad = (hi - lo) * 0.18 || 0.05;
  lo -= pad; hi += pad;
  const pos = (v) => `${Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100))}%`;
  // breakout (underperforming his skill, room to rise) = green; regression = red
  const tickColor = m.lean === 'underperforming' ? mobileColors.success : mobileColors.error;
  return (
    <div style={{ padding: '8px 0', borderBottom: `1px solid ${mobileColors.divider}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
        <span style={{ fontSize: 11, color: mobileColors.textTertiary }}>{m.label}</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: GOLD }}>~{fmtUnit(m.skill, m.unit)}</span>
          {m.actual != null && (
            <span style={{ fontSize: 10, color: mobileColors.textMuted }}>actual {fmtUnit(m.actual, m.unit)}</span>
          )}
        </span>
      </div>
      <div style={{ position: 'relative', height: 14 }}>
        <div style={{ position: 'absolute', top: 6, left: 0, right: 0, height: 2, background: mobileColors.divider, borderRadius: 1 }} />
        {m.lo != null && m.hi != null && (
          <div style={{ position: 'absolute', top: 4, left: pos(m.lo), width: `calc(${pos(m.hi)} - ${pos(m.lo)})`, height: 6, background: withAlpha(C.goldBase, 0.28), borderRadius: 3 }} />
        )}
        <div style={{ position: 'absolute', top: 2, left: pos(m.skill), width: 9, height: 9, marginLeft: -4.5, borderRadius: '50%', background: GOLD, border: `1px solid ${mobileColors.background || C.black}` }} />
        {m.actual != null && (
          <div style={{ position: 'absolute', top: 0, left: pos(m.actual), width: 2.5, height: 14, marginLeft: -1, background: tickColor, borderRadius: 1 }} />
        )}
      </div>
      <div style={{ fontSize: 9, color: mobileColors.textMuted, marginTop: 4 }}>
        ● skill-implied · ▭ likely range{m.actual != null ? ' · | actual' : ''}
      </div>
    </div>
  );
};

const ScoutingTabContent = ({ player }) => {
  const isPitcher = ['P', 'SP', 'RP'].includes(player?.info?.position);
  const a = player?.analytics || {};

  // --- Reliability (LIVE) ---
  const consistency = a.consistency;
  const hotCold = a.hot_cold;
  const streaks = a.streaks;
  const seasonStats = isPitcher ? player?.stats?.season?.pitching : player?.stats?.season?.hitting;
  const gamesPlayed = seasonStats?.games_played ?? seasonStats?.games;

  const hotColdColor = hotCold?.status === 'hot' ? mobileColors.success
    : hotCold?.status === 'cold' ? mobileColors.error
    : mobileColors.textTertiary;
  const hotColdLabel = hotCold?.status
    ? hotCold.status.charAt(0).toUpperCase() + hotCold.status.slice(1)
    : '—';

  const hasReliability = consistency || hotCold || streaks || gamesPlayed != null;

  // --- Skills-vs-luck + Verdict (LIVE — populated by the scouting seam) ---
  const scouting = player?.scouting;
  const verdict = scouting?.verdict;
  const skillsVsLuck = scouting?.skills_vs_luck;

  return (
    <div style={{ padding: '4px 12px 12px', color: mobileColors.textPrimary }}>
      {/* 1. Verdict line */}
      <div style={{ padding: '8px 0 6px', borderBottom: `1px solid ${mobileColors.divider}` }}>
        {verdict?.headline ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: GOLD, lineHeight: 1.3 }}>{verdict.headline}</div>
        ) : (
          <div style={{ fontSize: 12, fontWeight: 600, color: mobileColors.textMuted, fontStyle: 'italic' }}>Analysis pending</div>
        )}
      </div>

      {/* 2. Skills-vs-luck */}
      <SectionHead>Skills vs. Luck</SectionHead>
      {skillsVsLuck && Array.isArray(skillsVsLuck.metrics) && skillsVsLuck.metrics.length > 0 ? (
        <>
          {/* Option A: skill-implied value (dot) with its band (bar) vs the player's REAL actual (tick). */}
          <div style={{ fontSize: 10, color: mobileColors.textMuted, marginBottom: 2 }}>
            Skill-implied value (with range) vs the player&apos;s real stat
          </div>
          {skillsVsLuck.metrics.map((m) => (
            <SkillTrack key={m.key} m={m} />
          ))}
        </>
      ) : (
        <div style={{ padding: '12px 0', textAlign: 'center', color: mobileColors.textMuted, fontSize: 11, fontStyle: 'italic' }}>
          Insufficient data
        </div>
      )}

      {/* 3. Reliability (LIVE) */}
      <SectionHead>Reliability</SectionHead>
      {hasReliability ? (
        <>
          {consistency && consistency.score != null && (
            <>
              <Row
                label="Consistency"
                value={consistency.grade || '—'}
                note={`${consistency.score}%`}
                color={GOLD}
              />
              <div style={{ fontSize: 9, color: mobileColors.textMuted, margin: '1px 0 3px' }}>
                {/* DD-RO-BUGS6-0725 B3: switch on the served consistency.metric, NOT isPitcher — a
                    reliever's grade is scoreless-appearance rate, not quality starts, so gating on
                    isPitcher alone printed starter QS copy over a reliever's number. */}
                {consistency.metric === 'quality_start_rate'
                  ? '% of starts that are quality starts (6+ IP, ≤3 ER) — higher = more reliable.'
                  : consistency.metric === 'scoreless_appearance_rate'
                  ? '% of appearances with no earned runs allowed — higher = more reliable.'
                  : '% of games reaching base (hit, walk, or HBP) — higher = more reliable.'}
              </div>
            </>
          )}
          {hotCold && (
            <Row label="Form" value={hotColdLabel} color={hotColdColor} note={hotCold.temperature != null ? `${hotCold.temperature}` : null} />
          )}
          {streaks && (streaks.hit_streak != null || streaks.on_base_streak != null) && (
            <Row
              label={isPitcher ? 'Streak' : 'Hit streak'}
              value={`${streaks.hit_streak ?? streaks.on_base_streak ?? 0}`}
              color={mobileColors.textPrimary}
            />
          )}
          {gamesPlayed != null && (
            <Row label="Durability" value={`${gamesPlayed}`} note="games" color={mobileColors.textPrimary} />
          )}
        </>
      ) : (
        <div style={{ padding: '12px 0', textAlign: 'center', color: mobileColors.textMuted, fontSize: 11 }}>No reliability data</div>
      )}

      <div style={{ paddingTop: 10, fontSize: 9, color: mobileColors.textMuted }}>
        Trend lines & comps in the full profile.
      </div>
    </div>
  );
};

ScoutingTabContent.propTypes = { player: PropTypes.object };

export default ScoutingTabContent;
